import type { ReplicationDelta, ObjectSnapshot } from './protocol.js';

/** Version 1 key table: append-only; changing an alias requires a new negotiated version. */
export const WIRE_KEYS: readonly string[] = [
  'cooldownTicks', 'visualProfileId', 'netId', 'kind', 'replication', 'skillId', 'presentationKind', 'sourceId',
  'ownerNetId', 'stageId', 'radiusM', 'targetId', 'tick', 'type', 'shieldAbsorbed', 'x',
  'y', 'castForward', 'commonDashCooldownTicks', 'attributes', 'hitPoint', 'hpDamage', 'attackId', 'movementAllowed',
  'hpAfter', 'moveSpeedMps', 'basicAttack', 'vy', 'vx', 'statuses', 'lastProcessedInputSequence', 'connected',
  'crit', 'shield', 'weaponId', 'maxMana', 'maxHp', 'hp', 'importantUpdates', 'attackPresentation',
  'facing', 'mana', 'projectileRangeM', 'baselineTick', 'hitboxRadiusM', 'attackKey', 'center', 'down',
  'serverTick', 'archetypeId', 'meleeRangeM', 'cause', 'shape', 'instant', 'despawns', 'telegraph',
  'updates', 'delivery', 'events', 'roomId', 'spawns', 'charge', 'from', 'alive',
  'grade', 'level', 'to', 'durationS', 'profileId', 'geometry', 'previousProgress', 'contactPolicy',
  'scales', 'application', 'termination', 'angle', 'reach', 'width', 'hitPolicy', 'lifecycle',
  'placement', 'aimPoint', 'angleDeg', 'contacts', 'progress', 'remainingPreHitS', 'remainingCycleS', 'forward',
  'profile', 'inputSlotMap', 'motion', 'origin', 'presentation', 'reachM', 'swingForward', 'widthM',
  'owner', 'phase', 'actions', 'actualM', 'aimAngle', 'appliedServerTick', 'attack', 'autoSkillSlots',
  'basicAttackCommand', 'cc', 'clientTick', 'collided', 'definitionId', 'direction', 'disarm', 'displayName',
  'distanceM', 'dots', 'durationTicks', 'estimatedServerTick', 'expireServerTick', 'explicitPoint', 'explicitTargetId', 'facingRad',
  'fraction', 'frozen', 'impactRadiusM', 'instanceId', 'landed', 'marks', 'mode', 'moveX',
  'moveY', 'object', 'objects', 'orbitCenter', 'orbitRadiusM', 'playerNetId', 'playerSpeedMps', 'position',
  'postTickPosition', 'postTickTerrainCorrected', 'reason', 'reconnectToken', 'regionId', 'regions', 'remainingDistanceM', 'requestedM',
  'role', 'root', 'sequence', 'silence', 'skillSlot', 'slot', 'slow', 'snapshot',
  'stacks', 'start', 'startedServerTick', 'statusSummary', 'stun', 'tags', 'targetNetId', 'targetPoint',
  'thrust', 'tickRate', 'update', 'windupTicks',
  'cooldownReadyTicks', 'commonDashReadyTick', 'points',
];

export const COMPACT_ENCODING = 'compact-v1' as const;
export type WireEncoding = 'plain' | typeof COMPACT_ENCODING;
export const WIRE_MARK = '~';
const ALPHABET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
function aliasOf(index: number): string {
  return index < ALPHABET.length ? ALPHABET[index]! : ALPHABET[Math.floor(index / ALPHABET.length) - 1]! + ALPHABET[index % ALPHABET.length]!;
}
const KEY_TO_ALIAS = new Map(WIRE_KEYS.map((key, index) => [key, aliasOf(index)]));
const KEY_TO_PROPERTY = new Map(WIRE_KEYS.map((key, index) => [key, JSON.stringify(aliasOf(index)) + ':']));
const compactProperty = (key: string): string => KEY_TO_PROPERTY.get(key) ?? JSON.stringify(key) + ':';
const ALIAS_TO_KEY = new Map(WIRE_KEYS.map((key, index) => [aliasOf(index), key]));
if (KEY_TO_ALIAS.size !== WIRE_KEYS.length) throw new Error('WIRE_KEYS holds a duplicate');

export interface WireOptions {
  /** Send protocol keys as aliases. */
  readonly keys: boolean;
  /** Send each group of like spawns as a shared template plus what differs. */
  readonly templates: boolean;
}

type Json = null | boolean | number | string | readonly Json[] | { readonly [key: string]: Json };
type Plain = { [key: string]: unknown };
const isRecord = (value: unknown): value is Plain => value !== null && typeof value === 'object' && !Array.isArray(value);

function renameKeys(value: unknown, rename: (key: string) => string): unknown {
  if (Array.isArray(value)) return value.map((item) => renameKeys(item, rename));
  if (!isRecord(value)) return value;
  const out: Plain = {};
  for (const [key, item] of Object.entries(value)) out[rename(key)] = renameKeys(item, rename);
  return out;
}

const SPAWN_TEMPLATE_KEY = 'tpl';
const SPAWN_REF_KEY = 'tp';

/** The spawns that look alike: a monster of one archetype and grade, an attack of one skill stage and owner. Players never group. */
function spawnGroupOf(spawn: { kind?: unknown; archetypeId?: unknown; grade?: unknown; skillId?: unknown; stageId?: unknown; ownerNetId?: unknown }): string | undefined {
  if (spawn.kind === 'monster') return `m|${String(spawn.archetypeId)}|${String(spawn.grade)}`;
  if (spawn.kind === 'attack') return `a|${String(spawn.skillId)}|${String(spawn.stageId)}|${String(spawn.ownerNetId)}`;
  return undefined;
}

function templateSpawns(delta: Plain): Plain {
  const spawns = delta.spawns;
  if (!Array.isArray(spawns) || spawns.length < 2) return delta;
  const groups = new Map<string, Plain[]>();
  for (const spawn of spawns as Plain[]) {
    const group = spawnGroupOf(spawn);
    if (group !== undefined) {
      const members = groups.get(group);
      if (members) members.push(spawn); else groups.set(group, [spawn]);
    }
  }
  const templates: Plain[] = []; const templateOf = new Map<Plain, number>(); const shared = new Map<Plain, Plain>();
  for (const members of groups.values()) {
    if (members.length < 2) continue;
    const common: Plain = {};
    for (const [key, value] of Object.entries(members[0]!)) {
      if (key === 'netId') continue;
      const serialized = JSON.stringify(value);
      if (members.every((member) => member[key] === value || JSON.stringify(member[key]) === serialized)) common[key] = value;
    }
    const index = templates.push(common) - 1;
    for (const member of members) { templateOf.set(member, index); shared.set(member, common); }
  }
  if (templates.length === 0) return delta;
  const rows = (spawns as Plain[]).map((spawn) => {
    const common = shared.get(spawn);
    if (common === undefined) return spawn;
    return { [SPAWN_REF_KEY]: templateOf.get(spawn)!, ...Object.fromEntries(Object.entries(spawn).filter(([key]) => !(key in common))) };
  });
  return { ...delta, spawns: rows, [SPAWN_TEMPLATE_KEY]: templates };
}

function expandSpawns(delta: Plain): Plain {
  const templates = delta[SPAWN_TEMPLATE_KEY];
  if (!Array.isArray(templates) || !Array.isArray(delta.spawns)) return delta;
  const { [SPAWN_TEMPLATE_KEY]: _templates, ...rest } = delta;
  return { ...rest, spawns: (delta.spawns as Plain[]).map((row) => {
    const index = row[SPAWN_REF_KEY];
    if (typeof index !== 'number') return row;
    if (!Number.isInteger(index) || index < 0 || !isRecord(templates[index])) throw new Error('invalid spawn template');
    const { [SPAWN_REF_KEY]: _ref, ...own } = row;
    return { ...(templates[index] as Plain), ...own };
  }) };
}

/** Compacts a message for the wire. A message already holding a key that collides with an alias is refused, never mangled. */
export function encodeWire(message: Plain, options: WireOptions): Plain {
  let out = options.keys ? message : renameKeys(message, (key) => key) as Plain;
  if (options.templates && isRecord(out.delta)) out = { ...out, delta: templateSpawns(out.delta) };
  if (options.keys) {
    out = renameKeys(out, (key) => {
      const alias = KEY_TO_ALIAS.get(key);
      if (alias !== undefined) return alias;
      if (ALIAS_TO_KEY.has(key) || key === WIRE_MARK) throw new Error(`wire key ${JSON.stringify(key)} collides with an alias`);
      return key;
    }) as Plain;
  }
  return { [WIRE_MARK]: (options.keys ? 1 : 0) | (options.templates ? 2 : 0), ...out };
}

/** The inverse of {@link encodeWire}; a message without the mark is returned as it came. */
export function decodeWire(message: Plain): Plain {
  const mark = message[WIRE_MARK];
  if (mark === undefined) return message;
  if (typeof mark !== 'number' || !Number.isInteger(mark) || mark < 0 || mark > 3) throw new Error('unsupported wire flags');
  const { [WIRE_MARK]: _mark, ...rest } = message;
  let out: Plain = rest;
  if ((mark & 1) !== 0) out = renameKeys(out, (key) => ALIAS_TO_KEY.get(key) ?? key) as Plain;
  if ((mark & 2) !== 0 && isRecord(out.delta)) out = { ...out, delta: expandSpawns(out.delta) };
  return renameKeys(out, (key) => key) as Plain;
}

export type { Json as WireJson };

/** Serialize precisely the application payload sent by the socket adapter. Handshakes stay readable. */
export function deltaWireMessage(delta: import('./protocol.js').ReplicationDelta, encoding: WireEncoding): Plain {
  const message = { type: 'delta', delta };
  return encoding === COMPACT_ENCODING ? encodeWire(message, { keys: true, templates: true }) : message;
}
const utf8 = new TextEncoder();
export function wireBytes(value: unknown): number { return utf8.encode(JSON.stringify(value)).length; }
/** Disposable rows do not use spawn templates, so their cost is independent of the frame. */
export function wireRowBytes(value: unknown, encoding: WireEncoding): number {
  return wireBytes(encoding === COMPACT_ENCODING ? renameKeys(value, (key) => KEY_TO_ALIAS.get(key) ?? key) : value);
}
interface Fragment { readonly json: string; readonly bytes: number; }
interface Header extends Fragment { readonly fields: ReadonlyMap<string, string>; }
interface SpawnRow { readonly value: ObjectSnapshot; readonly variants: Map<string, Fragment>; }
interface CommonField { readonly value: unknown; examined: number; readonly exceptions: ObjectSnapshot[]; }
interface CommonGroup { readonly members: ObjectSnapshot[]; readonly seen: Set<ObjectSnapshot>; readonly fields: Map<string, CommonField>; }
interface SpawnFragments { readonly rows: Fragment; readonly templates?: Fragment; }
interface SpawnGroup { readonly rows: readonly SpawnRow[]; readonly commonKeys: ReadonlySet<string>; readonly template: Fragment; }
interface SequenceEntry<T> { readonly members: readonly object[]; value?: T; }
function sequenceEntry<T>(entries: SequenceEntry<T>[], values: readonly object[]): SequenceEntry<T> {
  let entry = entries.find((candidate) => candidate.members.length === values.length
    && candidate.members.every((member, index) => member === values[index]));
  if (!entry) { entry = { members: values }; entries.push(entry); }
  return entry;
}
const scheduledPayloads = new WeakMap<ReplicationDelta, { encoding: WireEncoding; parts: readonly (string | Fragment)[]; json?: string }>();

/** The socket sends the same serialized fragments used for budget admission. */
export function serializeDelta(delta: ReplicationDelta, encoding: WireEncoding): string {
  const scheduled = scheduledPayloads.get(delta);
  if (scheduled?.encoding !== encoding) return JSON.stringify(deltaWireMessage(delta, encoding));
  return scheduled.json ??= scheduled.parts.map((part) => typeof part === 'string' ? part : part.json).join('');
}

/** One tick, one encoding. Keys are immutable row identities and ordered spawn membership. */
export class WireTickCache {
  private readonly rows = new Map<object, Fragment>();
  private readonly signatures = new Map<unknown, string>();
  private readonly encodedValues = new Map<unknown, string>();
  private readonly spawnRows = new Map<ObjectSnapshot, SpawnRow>();
  private readonly commonGroups = new Map<string, CommonGroup>();
  private readonly groups: SequenceEntry<SpawnGroup>[] = [];
  private readonly spawns: SequenceEntry<SpawnFragments>[] = [];
  private readonly headers = new Map<string, Header>();
  private readonly ids = new Map<string, Fragment>();
  constructor(readonly encoding: WireEncoding) {}

  row(value: object): Fragment {
    let fragment = this.rows.get(value);
    if (!fragment) {
      const json = JSON.stringify(this.encoding === COMPACT_ENCODING
        ? renameKeys(value, (key) => KEY_TO_ALIAS.get(key) ?? key) : value);
      fragment = { json, bytes: utf8.encode(json).length };
      this.rows.set(value, fragment);
    }
    return fragment;
  }

  array(values: readonly object[]): Fragment {
    const rows = values.map((value) => this.row(value));
    return this.join(rows);
  }

  despawns(values: readonly string[]): Fragment {
    return this.join(values.map((value) => {
      let fragment = this.ids.get(value);
      if (!fragment) {
        const json = JSON.stringify(value); fragment = { json, bytes: utf8.encode(json).length }; this.ids.set(value, fragment);
      }
      return fragment;
    }));
  }

  spawn(values: readonly ObjectSnapshot[]): SpawnFragments {
    const node = sequenceEntry(this.spawns, values);
    if (!node.value) {
      if (this.encoding === 'plain') node.value = { rows: this.array(values) };
      else {
        const groups = new Map<string, ObjectSnapshot[]>();
        for (const value of values) {
          const group = spawnGroupOf(value);
          if (group !== undefined) {
            const members = groups.get(group);
            if (members) members.push(value); else groups.set(group, [value]);
          }
        }
        const templates: Fragment[] = [];
        const templated = new Map<ObjectSnapshot, Fragment>();
        for (const [name, members] of groups) {
          if (members.length < 2) continue;
          const node = sequenceEntry(this.groups, members);
          if (!node.value) {
            const rows = members.map((member) => this.spawnRow(member));
            const common = this.commonFields(name, members);
            node.value = { rows, commonKeys: new Set(common.map(([key]) => key)), template: this.object(common.map(([key, value]) => this.spawnField(key, value))) };
          }
          const { rows, commonKeys, template } = node.value;
          const index = templates.length;
          templates.push(template);
          const variantKey = JSON.stringify([index, ...commonKeys]);
          rows.forEach((row, i) => {
            let fragment = row.variants.get(variantKey);
            if (!fragment) {
              const fields = ['"tp":' + index];
              for (const [key, value] of Object.entries(row.value)) {
                if (!commonKeys.has(key) && value !== undefined && typeof value !== 'function' && typeof value !== 'symbol') fields.push(this.spawnField(key, value));
              }
              fragment = this.object(fields);
              row.variants.set(variantKey, fragment);
            }
            templated.set(members[i]!, fragment);
          });
        }
        node.value = {
          rows: this.join(values.map((value) => templated.get(value) ?? this.row(value))),
          ...(templates.length > 0 ? { templates: this.join(templates) } : {}),
        };
      }
    }
    return node.value;
  }

  private spawnRow(value: ObjectSnapshot): SpawnRow {
    let row = this.spawnRows.get(value);
    if (!row) {
      row = { value, variants: new Map() }; this.spawnRows.set(value, row);
    }
    return row;
  }

  /** A subset can only disagree with a common value at an observed exception row. */
  private commonFields(name: string, members: readonly ObjectSnapshot[]): [string, unknown][] {
    let group = this.commonGroups.get(name);
    if (!group) { group = { members: [], seen: new Set(), fields: new Map() }; this.commonGroups.set(name, group); }
    for (const member of members) if (!group.seen.has(member)) { group.seen.add(member); group.members.push(member); }
    const selected = new Set(members);
    return Object.entries(members[0]!).filter(([key, value]) => {
      if (key === 'netId' || value === undefined || typeof value === 'function' || typeof value === 'symbol') return false;
      if (!this.sameField(value, Reflect.get(members[1]!, key))) return false;
      let field = group.fields.get(key);
      if (!field) { field = { value: Reflect.get(group.members[0]!, key), examined: 0, exceptions: [] }; group.fields.set(key, field); }
      for (; field.examined < group.members.length; field.examined++) {
        const member = group.members[field.examined]!;
        if (!this.sameField(field.value, Reflect.get(member, key))) field.exceptions.push(member);
      }
      return this.sameField(value, field.value)
        ? !field.exceptions.some((member) => selected.has(member))
        : members.every((member) => this.sameField(value, Reflect.get(member, key)));
    });
  }

  private sameField(a: unknown, b: unknown): boolean { return a === b || this.spawnSignature(a) === this.spawnSignature(b); }

  private spawnSignature(value: unknown): string | undefined {
    let signature = this.signatures.get(value);
    if (signature === undefined) {
      signature = JSON.stringify(value);
      if (signature !== undefined) this.signatures.set(value, signature);
    }
    return signature;
  }

  private spawnField(key: string, value: unknown): string {
    let json = this.encodedValues.get(value);
    if (json === undefined) {
      json = JSON.stringify(renameKeys(value, (name) => KEY_TO_ALIAS.get(name) ?? name));
      this.encodedValues.set(value, json);
    }
    return compactProperty(key) + json;
  }

  private object(fields: readonly string[]): Fragment {
    const json = '{' + fields.join(',') + '}';
    return { json, bytes: utf8.encode(json).length };
  }
  private join(rows: readonly Fragment[]): Fragment {
    let json: string | undefined;
    return { get json() { return json ??= '[' + rows.map((row) => row.json).join(',') + ']'; },
      bytes: 2 + rows.reduce((sum, row) => sum + row.bytes, Math.max(0, rows.length - 1)) };
  }

  /** Fixed envelope with empty arrays; scalar values and their property order are part of the key. */
  header(delta: ReplicationDelta): Header {
    const scalars = Object.entries(delta).filter(([, value]) => !Array.isArray(value));
    const key = JSON.stringify(scalars);
    let header = this.headers.get(key);
    if (!header) {
      const json = JSON.stringify(deltaWireMessage({ ...delta, spawns: [], despawns: [], importantUpdates: [], updates: [], events: [] }, this.encoding));
      const fields = new Map(scalars.map(([name, value]) => [name,
        (this.encoding === COMPACT_ENCODING ? compactProperty(name) : JSON.stringify(name) + ':') + JSON.stringify(value)]));
      header = { json, bytes: utf8.encode(json).length, fields }; this.headers.set(key, header);
    }
    return header;
  }

  retain(delta: ReplicationDelta, spawn: SpawnFragments, despawns: Fragment, header: Header): void {
    const compact = this.encoding === COMPACT_ENCODING;
    const prefix = compact ? '{"~":3,' + compactProperty('type') + '"delta","delta":{' : '{"type":"delta","delta":{';
    const parts: (string | Fragment)[] = [prefix];
    Object.entries(delta).forEach(([key, value], index) => {
      if (index > 0) parts.push(',');
      const scalar = header.fields.get(key);
      if (scalar !== undefined) { parts.push(scalar); return; }
      parts.push(compact ? compactProperty(key) : JSON.stringify(key) + ':');
      parts.push(key === 'spawns' ? spawn.rows : key === 'despawns' ? despawns : this.array(value));
    });
    if (spawn.templates) parts.push(',"tpl":', spawn.templates);
    parts.push('}}');
    scheduledPayloads.set(delta, { encoding: this.encoding, parts });
  }
}
